import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, ArrowRight, CalendarClock, Sparkle, Check } from "lucide-react";
import { http } from "@/lib/api";
import { BrandLockup } from "@/components/Brand";
import { toast } from "sonner";

/**
 * Public consultation booking — Tuesday-only 30-min slots + fallback contact
 * form when no slot fits. Always in-person, no deposit, no payment.
 */
export default function Consult() {
  const navigate = useNavigate();
  const [slots, setSlots] = useState(null);
  const [pickedSlot, setPickedSlot] = useState(null);
  const [submitted, setSubmitted] = useState(null); // { mode: "booked"|"lead", name }
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", project_description: "" });

  useEffect(() => {
    http.get("/public/consultation-slots")
      .then(({ data }) => setSlots(data))
      .catch(() => setSlots([]));
  }, []);

  const grouped = useMemo(() => {
    if (!slots) return [];
    const byDay = new Map();
    for (const s of slots) {
      const d = new Date(s.start).toDateString();
      if (!byDay.has(d)) byDay.set(d, []);
      byDay.get(d).push(s);
    }
    return Array.from(byDay.entries()).map(([d, items]) => ({
      dayLabel: new Date(items[0].start).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }),
      items,
    }));
  }, [slots]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setBusy(true);
    try {
      if (pickedSlot) {
        await http.post("/public/consultation/book", { slot_id: pickedSlot.id, ...form });
        setSubmitted({ mode: "booked", name: form.name, slot: pickedSlot });
      } else {
        await http.post("/public/consultation/lead", form);
        setSubmitted({ mode: "lead", name: form.name });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return <ConsultConfirmation result={submitted} onHome={() => navigate("/")} />;
  }

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/30 border-b border-[#D2A853]/15">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center"><BrandLockup size="sm" /></Link>
          <Link to="/" className="overline text-[#A3A8A4] hover:text-[#D2A853] inline-flex items-center gap-2" data-testid="consult-back-home">
            <ArrowLeft className="w-3.5 h-3.5" /> Home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
        <div className="text-center stagger-child" style={{ animationDelay: "100ms" }}>
          <span className="overline text-[#D2A853]">Book a consultation</span>
          <h1 className="font-display text-5xl sm:text-6xl text-[#E8E9E3] mt-3 tracking-tight">Pick a Time</h1>
          <div className="ornate-rule mt-6 max-w-xs mx-auto"><Sparkle className="w-3 h-3" strokeWidth={1.4} /></div>
          <p className="mt-6 text-[#C8CFC9] max-w-xl mx-auto leading-relaxed">
            Consultations are <span className="text-[#D2A853]">in-person at the studio</span>, 30 minutes,
            held on Tuesday evenings.
          </p>
          <p className="mt-4 text-sm text-[#A3A8A4] max-w-xl mx-auto leading-relaxed" data-testid="consult-pricing-note">
            My half-day rate is <span className="text-[#D2A853]">$800</span> and full-day rate is{" "}
            <span className="text-[#D2A853]">$1,500</span>. Smaller projects are billed at{" "}
            <span className="text-[#D2A853]">$250 / hour</span>.
          </p>
        </div>

        {/* Slots */}
        <section className="mt-12" data-testid="consult-slots-section">
          {slots === null ? (
            <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 text-[#D2A853] animate-spin" /></div>
          ) : grouped.length === 0 ? (
            <NoSlotsHero />
          ) : (
            <>
              <div className="space-y-8">
                {grouped.map((g) => (
                  <div key={g.dayLabel}>
                    <div className="overline text-[#D2A853] text-[10px] mb-3">{g.dayLabel}</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {g.items.map((s) => {
                        const active = pickedSlot?.id === s.id;
                        return (
                          <button
                            key={s.id}
                            onClick={() => setPickedSlot(active ? null : s)}
                            className={`px-4 py-4 border transition text-sm tracking-wider ${
                              active
                                ? "border-[#D2A853] bg-[#D2A853]/15 text-[#D2A853]"
                                : "border-[#D2A853]/30 text-[#E8E9E3] hover:border-[#D2A853]/70 hover:bg-[#D2A853]/5"
                            }`}
                            data-testid={`consult-slot-${s.id}`}
                          >
                            {s.label}
                            {active && <Check className="w-3 h-3 inline ml-2" strokeWidth={2} />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>

        {/* Fallback notice when slots exist but maybe none work */}
        {slots && slots.length > 0 && !pickedSlot && (
          <p className="mt-10 text-center text-xs text-[#A3A8A4] max-w-lg mx-auto leading-relaxed" data-testid="consult-fallback-hint">
            Don&rsquo;t see a time that works? Submit your info below and I&rsquo;ll reach out to coordinate.
          </p>
        )}

        {/* Form (always rendered — used both for booking and lead capture) */}
        <section className="mt-12 frame-card p-8" data-testid="consult-form">
          <div className="flex items-center justify-between gap-3 mb-6">
            <span className="overline text-[#D2A853] text-[10px]">
              {pickedSlot
                ? `Booking ${new Date(pickedSlot.start).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`
                : "Or — request a different time"}
            </span>
            {pickedSlot && (
              <button
                onClick={() => setPickedSlot(null)}
                className="text-[10px] uppercase tracking-wider text-[#A3A8A4] hover:text-[#D2A853]"
                data-testid="consult-clear-slot"
              >
                Clear
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <FormField label="Full Name" required>
              <input className="input-line" value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="consult-name" />
            </FormField>
            <FormField label="Email" required>
              <input type="email" className="input-line" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="consult-email" />
            </FormField>
            <FormField label="Phone (optional)">
              <input type="tel" className="input-line" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="consult-phone" />
            </FormField>
            <FormField label="Short description of your project (optional)">
              <textarea
                rows={2}
                className="input-line resize-none"
                placeholder="A brief note — style, placement, size."
                value={form.project_description}
                onChange={(e) => update("project_description", e.target.value)}
                data-testid="consult-description"
              />
            </FormField>
          </div>

          <div className="mt-8 flex justify-end">
            <button
              onClick={submit}
              disabled={busy}
              className="btn-brass inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="consult-submit"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {pickedSlot ? "Confirm Consultation" : "Request a Time"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <p className="text-xs text-[#A3A8A4] mt-5 leading-relaxed">
            Consultations are free — no deposit required. We&rsquo;ll go over your design,
            size, and final pricing in person.
          </p>
        </section>
      </main>
    </div>
  );
}

function NoSlotsHero() {
  return (
    <div className="text-center py-12 px-6 frame-card" data-testid="consult-no-slots">
      <CalendarClock className="w-8 h-8 text-[#D2A853] mx-auto mb-4" strokeWidth={1.3} />
      <p className="text-[#E8E9E3] leading-relaxed max-w-xl mx-auto">
        Consultations are typically held Tuesday evenings.
      </p>
      <p className="text-sm text-[#A3A8A4] mt-3 max-w-xl mx-auto leading-relaxed">
        If these times don&rsquo;t work for you, submit your info below and I&rsquo;ll reach out to coordinate a time.
      </p>
    </div>
  );
}

function FormField({ label, required, children }) {
  return (
    <div>
      <label className="overline text-[#A3A8A4] block mb-2 text-[10px]">
        {label} {required && <span className="text-[#D2A853]">*</span>}
      </label>
      {children}
    </div>
  );
}

function ConsultConfirmation({ result, onHome }) {
  const isBooked = result.mode === "booked";
  return (
    <div className="min-h-screen flex items-center" data-testid="consult-confirmation">
      <div className="max-w-2xl mx-auto px-6 lg:px-12 py-16 text-center w-full">
        <Check className="w-12 h-12 text-[#D2A853] mx-auto mb-6" strokeWidth={1.4} />
        <span className="overline text-[#D2A853]">{isBooked ? "Consultation Confirmed" : "Request Received"}</span>
        <h1 className="font-display text-4xl sm:text-5xl text-[#E8E9E3] mt-3">
          Thank you{result.name ? `, ${result.name.split(" ")[0]}` : ""}.
        </h1>
        {isBooked ? (
          <>
            <p className="text-[#C8CFC9] leading-relaxed mt-6 max-w-lg mx-auto">
              You&rsquo;re booked for{" "}
              <span className="text-[#D2A853]">
                {new Date(result.slot.start).toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </span>{" "}
              at the studio. We&rsquo;ll go over your design, size, and final pricing during your consultation.
            </p>
            <p className="text-xs text-[#A3A8A4] mt-6 max-w-lg mx-auto">
              Confirmation details have been sent to your email.
            </p>
          </>
        ) : (
          <p className="text-[#C8CFC9] leading-relaxed mt-6 max-w-lg mx-auto">
            Brian will reach out shortly to coordinate a time. No deposit is required at this stage.
          </p>
        )}
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <button onClick={onHome} className="btn-brass inline-flex items-center gap-2" data-testid="consult-confirm-home">
            Back to Home <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
